import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import TabScrollButton from "@mui/material/TabScrollButton";
import {
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { ApiManager } from "../ApiClient";
import axios from "axios";
import { OutlinedInput } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IconHelper from "../Helpers/IconHelper";
import ColorHelper from "../Helpers/ColorHelper";
import "../Components/Ui-Components/SectionTitle2/SectionTitle2.css";
import { useNavigate } from "react-router-dom";
import HighlightsStyle from "../Components/Highlights/HighlightsStyle";
import "../Components/Highlights/highlight.css";

const EventCertificateManager = ({ onClose, onProceedToCertificate, resumeSelection, certificateData }) => {
  const [eventFields, setEventFields] = useState([
    {
      eventId: "",
      bibNumber: "",
      selectedImage: null,
      eventImages: [],
      pagination: null,
      achievement_id: null,
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [errors, setErrors] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [currentPages, setCurrentPages] = useState(eventFields.map(() => 1));
  const [pageLoading, setPageLoading] = useState(false);
  const [eventName, setEventName] = useState("");
  const [bibNumber, setBibNumber] = useState("");
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const navigate = useNavigate();
  const hasAutoFilled = useRef(false);

  // New state for family event logic
  const [isFamilyEvent, setIsFamilyEvent] = useState(false);
  const [bibCount, setBibCount] = useState(0);
  const [familyEventId, setFamilyEventId] = useState("");

  const [requiredSequence, setRequiredSequence] = useState([]);

  useEffect(() => {
    const storedEventName = localStorage.getItem("Selected Event");
    const storedBibId = localStorage.getItem("bibId");

    if (storedEventName) {
      setEventName(storedEventName);
    }

    if (storedBibId) {
      setBibNumber(storedBibId);
    }

    // Check for family event eligibility
    const achievementsEligibility = localStorage.getItem("achievementsEligibility");
    if (achievementsEligibility) {
      try {
        const parsedData = JSON.parse(achievementsEligibility);
        const eventType = parsedData.event_type;
        const count = parsedData.bib_count;

        // Check if event_type is "family_event" and bib_count is valid
        if (eventType === "family_event" && count && count > 0) {
          setIsFamilyEvent(true);
          setBibCount(count);
        } else {
          setIsFamilyEvent(false);
          console.log("Not a family event or invalid bib_count");
        }
      } catch (error) {
        console.error("Error parsing achievementsEligibility:", error);
      }
    }
  }, []);

  useEffect(() => {
    setCurrentPages(
      eventFields.map((field) => field.pagination?.current_page || 1)
    );
  }, [eventFields.length]);

  useEffect(() => {
    if (resumeSelection && certificateData && certificateData.eventFields.length > 0) {
      setEventFields(certificateData.eventFields);
      setSearchPerformed(true);
      setShowForm(false);
      setTabIndex(0);
    }
  }, [resumeSelection, certificateData]);

  const fetchEventsWithData = useCallback(async () => {
    if (events.length > 0) return;

    setEventsLoading(true);
    try {
      const response = await axios.get(ApiManager.homePage());
      if (response.data.StatusCode === 200) {
        const allEvents = [];
        Object.keys(response.data.result.slam_events).forEach((key) => {
          allEvents.push(...response.data.result.slam_events[key]);
        });
        setEvents(allEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setErrors(["Failed to load events. Please refresh the page."]);
    } finally {
      setEventsLoading(false);
    }
  }, [events.length]);

  const FontHelper = {
    fSemiBold: "inherit",
  };

  const handleImageLoad = (fieldIndex, imageId) => {
    setImageLoadingStates((prev) => ({
      ...prev,
      [`${fieldIndex}-${imageId}`]: false,
    }));
  };

  const handleImageLoadStart = (fieldIndex, imageId) => {
    setImageLoadingStates((prev) => ({
      ...prev,
      [`${fieldIndex}-${imageId}`]: true,
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Modified handleAddEventField for family event logic
  const handleAddEventField = () => {
    const selectedEventIds = eventFields
      .map((field) => field.eventId)
      .filter((id) => id);

    // For family events, check against bib_count
    if (isFamilyEvent) {
      if (eventFields.length >= bibCount) {
        toast.warning(`Maximum ${bibCount} entries allowed for this family event`, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 3000,
        });
        return;
      }

      // Add new field with same event ID but empty BIB
      setEventFields([
        ...eventFields,
        {
          eventId: familyEventId, // Auto-fill with same event
          bibNumber: "", // User must enter manually
          selectedImage: null,
          eventImages: [],
          pagination: null,
        },
      ]);
    } else {
      // Original logic for non-family events
      if (events.length > 1 && selectedEventIds.length < events.length) {
        setEventFields([
          ...eventFields,
          {
            eventId: "",
            bibNumber: "",
            selectedImage: null,
            eventImages: [],
            pagination: null,
          },
        ]);
      }
    }
  };

  const handleRemoveEventField = (index) => {
    if (eventFields.length > 1) {
      const newFields = eventFields.filter((_, i) => i !== index);
      setEventFields(newFields);
    }
  };

  const handleEventSelection = (index, eventId) => {
    const newFields = [...eventFields];
    newFields[index] = { ...newFields[index], eventId };
    setEventFields(newFields);
  };

  const handleBibNumberChange = (index, value) => {
    const newFields = [...eventFields];
    newFields[index] = { ...newFields[index], bibNumber: value };
    setEventFields(newFields);
  };

  const getAvailableEvents = (currentIndex) => {
    // For family events, return only the selected family event
    if (isFamilyEvent && familyEventId) {
      return events.filter((event) => event.event_id === familyEventId);
    }

    // Original logic for non-family events
    const selectedEventIds = eventFields
      .map((field, index) => (index !== currentIndex ? field.eventId : null))
      .filter((id) => id !== null && id !== "");
    return events.filter((event) => !selectedEventIds.includes(event.event_id));
  };

  const getEventNameById = (eventId) => {
    const event = events.find((e) => e.event_id === eventId);
    return event ? event.event_name : eventId;
  };

  const validateForm = () => {
    const newErrors = [];
    eventFields.forEach((field, index) => {
      if (!field.eventId) {
        newErrors.push(`Please select an event for field ${index + 1}`);
      }
      if (!field.bibNumber.trim()) {
        newErrors.push(`Please enter BIB number for field ${index + 1}`);
      }
    });

    // For non-family events, check for unique event IDs
    if (!isFamilyEvent) {
      const eventIds = eventFields
        .map((field) => field.eventId)
        .filter((id) => id !== "");
      const uniqueEventIds = [...new Set(eventIds)];
      if (eventIds.length !== uniqueEventIds.length) {
        newErrors.push("Please select different events for each field");
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const fetchImagesForPage = async (fieldIndex, page) => {
    const field = eventFields[fieldIndex];
    setPageLoading(true);

    try {
      const formData = new FormData();
      formData.append("event_id", field.eventId);
      formData.append("bib_number", field.bibNumber);
      formData.append("_token", localStorage.getItem("session_token"));
      formData.append("page", page);
      formData.append("per_page", field.pagination?.per_page || 50);

      const response = await axios.post(
        ApiManager.paginateEventPhotos(),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const apiData = response.data;

      if (apiData.HasError || !apiData.result?.photos) {
        throw new Error(apiData.Message || "No photos found");
      }

      const photos = apiData.result.photos;
      const pagination = apiData.result.pagination;

      const newFields = [...eventFields];
      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        eventImages: photos,
        pagination: pagination,
      };

      setEventFields(newFields);
    } catch (error) {
      console.error("Error fetching images for page:", error);
      setErrors(["Failed to fetch images for page. Please try again."]);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearchAchievements = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      const formData = new FormData();
      const maxPairs = isFamilyEvent ? bibCount : 4;
      for (let i = 0; i < maxPairs; i++) {
        if (
          i < eventFields.length &&
          eventFields[i].eventId &&
          eventFields[i].bibNumber
        ) {
          formData.append(
            `event_bib_pairs[${i}][event_id]`,
            eventFields[i].eventId
          );
          formData.append(
            `event_bib_pairs[${i}][bib_number]`,
            eventFields[i].bibNumber
          );
        } else {
          formData.append(`event_bib_pairs[${i}][event_id]`, "");
          formData.append(`event_bib_pairs[${i}][bib_number]`, "");
        }
      }
      formData.append("_token", localStorage.getItem("session_token"));

      // Achievement_id, bib_count, and event_type from localStorage
      const achievementsEligibility = localStorage.getItem("achievementsEligibility");
      if (achievementsEligibility) {
        try {
          const parsedData = JSON.parse(achievementsEligibility);
          if (parsedData.achievement_id) {
            formData.append("achievement_id", parsedData.achievement_id);
          }
          if (parsedData.bib_count) {
            formData.append("bib_count", parsedData.bib_count);
          }
          if (parsedData.event_type) {
            formData.append("event_type", parsedData.event_type);
          }
        } catch (error) {
          console.error("Error parsing achievementsEligibility:", error);
        }
      }
      
      formData.append("page", 1);

      const response = await axios.post(
        ApiManager.achievementsSearch(),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to search images.");
      }

      // Validate that results is an array
      const results = Array.isArray(response.data.data?.pairs) ? response.data.data.pairs : [];
      if (!results || results.length === 0) {
        throw new Error("No results found for the provided event-bib combinations.");
      }

      //  EXTRACT AND STORE required_sequence from API response
      const eligible = response.data.data?.eligible || [];
      if (eligible.length > 0 && eligible[0].required_sequence) {
        setRequiredSequence(eligible[0].required_sequence);
        console.log(" Required Sequence captured:", eligible[0].required_sequence);
      } else {
        console.warn("⚠️ No required_sequence found in API response");
        setRequiredSequence([]);
      }

      const updatedFields = eventFields.map((field, index) => {
        // Safely find result with array validation
        const result = Array.isArray(results) ? results.find(
          (r) =>
            r && r.event_id === field.eventId && r.bib_number === field.bibNumber
        ) : null;
        return {
          ...field,
          eventImages: result ? (result.photos || result.images || []) : [],
          pagination: result ? result.pagination : null,
        };
      });

      // Check if any field has no images found
      const fieldsWithoutImages = updatedFields.filter(field =>
        field.eventImages.length === 0 && field.eventId
      );

      if (fieldsWithoutImages.length > 0) {
        const missingEventNames = fieldsWithoutImages.map(field => {
          const eventName = getEventNameById(field.eventId);
          return `${eventName} (BIB: ${field.bibNumber})`;
        }).join(", ");
        throw new Error(`No photos found for: ${missingEventNames}. Please verify your event and BIB number.`);
      }

      setEventFields(updatedFields);
      setSearchPerformed(true);
      setShowForm(false);
    } catch (error) {
      console.error("Error searching images:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to search images. Please check your event and BIB number.";
      setErrors([errorMessage]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };
 
  const handleImageSelection = (fieldIndex, image) => {
    const newFields = [...eventFields];
    const currentField = newFields[fieldIndex];
    if (currentField.selectedImage?.id === image.id) {
      newFields[fieldIndex] = {
        ...currentField,
        selectedImage: null,
      };
    } else {
      newFields[fieldIndex] = {
        ...currentField,
        selectedImage: image,
      };
    }
    setEventFields(newFields);
  };

  const validateFinalSelections = () => {
    const hasMissingSelection = eventFields.some((field) => !field.selectedImage);

    const newErrors = [];
    if (hasMissingSelection) {
      newErrors.push("Please select an image for each event");
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleProceedToCertificate = async () => {
    if (validateFinalSelections()) {
      setLoading(true);
      setErrors([]);

      try {
        const formData = new FormData();

        const achievementsEligibility = localStorage.getItem(
          "achievementsEligibility"
        );
        let achievementId = null;
        if (achievementsEligibility) {
          try {
            const parsedData = JSON.parse(achievementsEligibility);
            achievementId = parsedData.achievement_id;
          } catch (parseError) {
            console.error("Error parsing achievementsEligibility:", parseError);
          }
        }
        if (achievementId) {
          formData.append("achievement_id", achievementId);
        } else {
          console.log("No achievement_id found in localStorage");
        }

        eventFields.forEach((field) => {
          if (field.selectedImage) {
            formData.append(
              `selected_photos[${field.eventId}][]`,
              field.selectedImage.id
            );
          }
        });
        
        const response = await axios.post(
          ApiManager.templateDetails(),
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        const apiData = response.data.data || response.data;
        const photosArray = apiData.photos || [];
        const templateData = apiData.template || apiData.result || {};
        const achievementDetails = apiData.achievement_details || {};

        //  USE requiredSequence from state 
        const templateDataToStore = {
          required_sequence: requiredSequence,
          template_config: templateData.template_config || {},
          template_image: templateData.template_image || "",
        };
      
        localStorage.setItem(
          "event_template_data",
          JSON.stringify(templateDataToStore)
        );
        localStorage.setItem(
          "achievement_details",
          JSON.stringify(achievementDetails)
        )

        if (photosArray.length > 0) {
          localStorage.setItem(
            "event_photos_data",
            JSON.stringify(photosArray)
          );
        } else {
          console.warn("No photos found in API response to store.");
        }

        if (response.data.success === true) {
          const updatedEventFields = eventFields.map((field) => {
            const photo = photosArray.find(
              (p) =>
                p.event_id === field.eventId &&
                p.photo_id === field.selectedImage.id
            );
            return {
              ...field,
              achievement_id: achievementId,
              selectedImage: {
                ...field.selectedImage,
                high_res_url: photo
                  ? photo.high_res_url
                  : field.selectedImage.s3,
              },
              participant_details: photo?.participant_details || null,
            };
          });

          const certificateData = {
            eventFields: updatedEventFields,
            type: "procam-slam",
            achievement_id: achievementId,
            templateDetails: {
              ...templateData,
              //  INJECT required_sequence into templateDetails
              template: {
                ...templateData,
                config: {
                  ...(templateData.config || {}),
                  required_sequence: requiredSequence //  Pass from state
                }
              }
            },
            photos: photosArray,
            achievement_details: achievementDetails,
            handleEditForm,
          };

          console.log(" Passing certificateData with required_sequence:", certificateData.templateDetails.template.config.required_sequence);

          onProceedToCertificate(certificateData);
        } else {
          throw new Error("Failed to fetch template details.");
        }
      } catch (error) {
        console.error("Error fetching template details:", error);
        setErrors(["Failed to fetch template details. Please try again."]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditForm = () => {
    setShowForm(true);
    setSearchPerformed(false);
    setErrors([]);
    const resetFields = eventFields.map((field) => ({
      ...field,
      selectedImage: null,
      eventImages: [],
      pagination: null,
    }));
    setEventFields(resetFields);
    setTabIndex(0);
  };

  const handlePageChange = (fieldIndex, event, page) => {
    const newFields = [...eventFields];
    const field = newFields[fieldIndex];

    newFields[fieldIndex] = {
      ...field,
      pagination: {
        ...field.pagination,
        current_page: page,
      },
    };
    setEventFields(newFields);

    fetchImagesForPage(fieldIndex, page);
  };

  useEffect(() => {
    fetchEventsWithData();
  }, [fetchEventsWithData]);

  // Modified auto-fill logic for family events
  useEffect(() => {
    if (
      !hasAutoFilled.current &&
      eventName &&
      bibNumber &&
      events.length > 0 &&
      !eventsLoading &&
      eventFields.length === 1 &&
      eventFields[0].eventId === ""
    ) {
      const incoming = eventName.trim().toLowerCase();
      const matchedEvent = events.find((e) => {
        const api = e.event_name?.trim().toLowerCase();
        return api === incoming || api.includes(incoming) || incoming.includes(api);
      });

      if (matchedEvent) {
        setEventFields([
          {
            ...eventFields[0],
            eventId: matchedEvent.event_id,
            bibNumber: bibNumber,
          },
        ]);

        // Store family event ID for future fields
        if (isFamilyEvent) {
          setFamilyEventId(matchedEvent.event_id);
        }

        hasAutoFilled.current = true;
      } else {
        if (events.length === 1) {
          setEventFields([
            {
              ...eventFields[0],
              eventId: events[0].event_id,
              bibNumber: bibNumber,
            },
          ]);

          if (isFamilyEvent) {
            setFamilyEventId(events[0].event_id);
          }

          hasAutoFilled.current = true;
        } else {
          console.warn("⚠️ Event not found in API results:", eventName);
        }
      }
    }
  }, [eventName, bibNumber, events, eventsLoading, eventFields, isFamilyEvent]);

  useEffect(() => {
    if (errors.length > 0) {
      errors.forEach((error, index) => {
        toast.error(error, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          toastId: `error-${index}`,
          style: {
            backgroundColor: "#FFFFFF",
            color: "#3F0E77",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
          progressStyle: {
            backgroundColor: "#3F0E77",
          },
          closeButton: true,
        });
      });
    }
  }, [errors]);

  const selectedEventIds = eventFields
    .map((field) => field.eventId)
    .filter((id) => id);

  // Modified logic for Add button disable state
  const isAddDisabled = isFamilyEvent
    ? eventFields.length >= bibCount
    : (events.length === 1 || (events.length > 1 && selectedEventIds.length >= events.length));

  const showRemoveInsteadOfAdd = isAddDisabled && eventFields.length > 1;

  const selectedCount = eventFields.filter(
    (field) => field.selectedImage !== null
  ).length;

  return (
    <Container maxWidth="xl" sx={{ py: 3, pb: 18 }}>
      {showForm && (
        <Box sx={{ p: 0, mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "8px",
              gap: 2,
              marginBottom: '30px'
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                onClick={() => navigate("/products")}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#E7E0F5",
                  },
                }}
              >
                <IconHelper.LeftArrow />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 16, sm: 18, md: 20 },
                  fontWeight: 600,
                  color: "#3F0E77",
                  fontFamily: "inherit",
                }}
              >
                Select Events and Enter BIB Numbers
              </Typography>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                height: "1px",
                backgroundColor: ColorHelper.LightPurple,
                mx: 3,
              }}
            ></Box>
          </Box>
          {eventsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#3F0E77" }} />
              <Typography sx={{ ml: 2, color: "#3F0E77" }}>
                Loading events...
              </Typography>
            </Box>
          ) : (
            <Box>
              {eventFields.map((field, index) => {
                const isLast = index === eventFields.length - 1;
                const isValid = field.eventId && field.bibNumber;

                return (
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    key={index}
                    sx={{ mb: 2 }}
                  >
                    <Grid item xs={12} md={5}>
                      <FormControl
                        fullWidth
                        sx={{
                          "& label.Mui-focused": {
                            color: "#3F0E77",
                          },
                          "& .MuiOutlinedInput-root": {
                            height: "55px",
                            borderRadius: "4px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3F0E77",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3F0E77",
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                              color: "#3F0E77",
                            },
                          },
                        }}
                      >
                        <InputLabel>Select Event</InputLabel>
                        <Select
                          value={field.eventId}
                          onChange={(e) =>
                            handleEventSelection(index, e.target.value)
                          }
                          input={<OutlinedInput label="Select Event" />}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "56px",
                              borderRadius: "4px",
                            },
                          }}
                          disabled={events.length === 1 || isFamilyEvent}
                        >
                          {getAvailableEvents(index).map((event) => (
                            <MenuItem
                              key={event.event_id}
                              value={event.event_id}
                            >
                              {event.event_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="BIB Number"
                        variant="outlined"
                        value={field.bibNumber}
                        onChange={(e) =>
                          handleBibNumberChange(index, e.target.value)
                        }
                        placeholder="Enter BIB Number"
                        disabled={!field.eventId}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#3F0E77",
                          },
                          "& .MuiOutlinedInput-root": {
                            height: "55px",
                            borderRadius: "4px",
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3F0E77",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3F0E77",
                            },
                            "&.Mui-focused .MuiOutlinedInput-input": {
                              color: "#3F0E77",
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={2}
                      sx={{
                        display: "flex",
                        justifyContent: { xs: "center", md: "flex-start" },
                      }}
                    >
                      {isLast ? (
                        showRemoveInsteadOfAdd ? (
                          <IconButton
                            onClick={() => handleRemoveEventField(index)}
                            sx={{
                              bgcolor: "error.main",
                              color: "white",
                              "&:hover": { bgcolor: "error.dark" },
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            onClick={() => {
                              if (isValid) handleAddEventField();
                            }}
                            sx={{
                              bgcolor: "#3f0e77",
                              color: "white",
                              "&:hover": { bgcolor: "#2d035cff" },
                            }}
                            disabled={isAddDisabled}
                          >
                            <AddIcon />
                          </IconButton>
                        )
                      ) : (
                        <IconButton
                          onClick={() => handleRemoveEventField(index)}
                          sx={{
                            bgcolor: "error.main",
                            color: "white",
                            "&:hover": { bgcolor: "error.dark" },
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mr: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleSearchAchievements}
              size="small"
              sx={{
                borderColor: "#3F0E77",
                px: 4,
                py: 1,
                fontSize: "1rem",
                color: "#3F0E77",
                textTransform: "none",
                fontWeight: "500",
                "&:hover": {
                  bgcolor: "#3F0E77",
                  color: "#FFFFFF",
                  borderColor: "#3F0E77",
                },
              }}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Box>
        </Box>
      )}

      {searchPerformed && !showForm && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 16, sm: 18, md: 20 },
                fontWeight: 600,
                color: "#3F0E77",
                fontFamily: "inherit",
                mr: 3,
              }}
            >
              Select Image
            </Typography>

            <Box
              sx={{
                flexGrow: 1,
                height: "1px",
                backgroundColor: "#E0DFF5",
                mr: 2,
              }}
            />

            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditForm}
              size="large"
              sx={{
                borderColor: "#3F0E77",
                color: "#3F0E77",
                textTransform: "none",
                borderRadius: 1,
                fontWeight: "500",
                fontSize: { xs: "0.70rem", sm: "0.875rem", md: "1rem" },
                padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px" },
                "& .MuiButton-startIcon": {
                  marginRight: { xs: 0.5, sm: 1 },
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                  },
                },
                "&:hover": {
                  bgcolor: "#3F0E77",
                  color: "#FFFFFF",
                  borderColor: "#3F0E77",
                },
                minWidth: { xs: "auto", sm: "120px" },
              }}
            >
              Edit Search
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              minHeight: "auto",
              marginBottom: "20px"
            }}
          >
            <Box
              sx={{
                width: "100vw",
                padding: { xs: "10px 5px", lg: "8px 5px" },
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                height: 'auto',
                backgroundColor: ColorHelper.LightPurple,
                color: ColorHelper.BlueColor,
                fontFamily: FontHelper.fRegular,
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              Select one image per event
            </Box>
          </Box>
          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: { backgroundColor: "#3F0E77", borderRadius: 2, height: 0 },
              }}
              sx={{
                "& .MuiTabScrollButton-root": {
                  backgroundColor: "transparent !important",
                  "&:hover": {
                    backgroundColor: "transparent !important",
                  },
                },
              }}
              ScrollButtonComponent={(props) => {
                const { direction, onClick, disabled, ...rest } = props;

                const handleScrollButtonClick = (event) => {
                  if (typeof onClick === "function") onClick(event);
                  if (direction === "left") {
                    setTabIndex((prev) => (prev > 0 ? prev - 1 : prev));
                  } else if (direction === "right") {
                    setTabIndex((prev) =>
                      prev < eventFields.length - 1 ? prev + 1 : prev
                    );
                  }
                };
                return (
                  <TabScrollButton
                    {...rest}
                    direction={direction}
                    onClick={handleScrollButtonClick}
                    disabled={disabled}
                  />
                );
              }}
            >
              {eventFields.map((field, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: tabIndex === index ? "#3F0E77" : "inherit",
                        }}
                      >
                        {getEventNameById(field.eventId)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: tabIndex === index ? "#3F0E77" : "text.secondary",
                          fontWeight: "bold",
                        }}
                      >
                        {field.bibNumber}
                      </Typography>
                      {field.selectedImage && (
                        <CheckIcon sx={{ color: "#3F0E77" }} fontSize="small" />
                      )}
                    </Box>
                  }
                  sx={{
                    minWidth: "auto",
                    padding: "6px 16px",
                    border:
                      tabIndex === index ? "2px solid #3F0E77" : "1px solid #d3d3d3",
                    borderRadius: "8px",
                    backgroundColor: tabIndex === index ? "#F3E5F5" : "#fafafa",
                    color: tabIndex === index ? "#3F0E77" : "black",
                    mx: 0.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#B39DDB",
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Paper>

          {eventFields.map((field, fieldIndex) => (
            <Box
              key={fieldIndex}
              role="tabpanel"
              hidden={tabIndex !== fieldIndex}
              id={`tabpanel-${fieldIndex}`}
              sx={{ width: "100%" }}
            >
              {tabIndex === fieldIndex && (
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, fontWeight: "bold" }}
                    >
                      {field.eventImages.length > 0
                        ? `Found ${field.pagination?.total || field.eventImages.length
                        } Photos - Click to Select`
                        : "No Images Found"}
                    </Typography>

                    {field.eventImages.length > 0 ? (
                      <>
                        {pageLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: 250,
                            }}
                          >
                            <CircularProgress sx={{ color: "#3F0E77" }} />
                          </Box>
                        ) : (
                          <div className="evntGenCert container"
                            style={{ maxWidth: "1400px", width: "100%", boxShadow: "none" }}
                          >
                            {field.eventImages
                              .slice(
                                (currentPages[fieldIndex] - 1) *
                                (field.pagination?.per_page || 50),
                                currentPages[fieldIndex] *
                                (field.pagination?.per_page || 50)
                              )
                              .map((image, index) => (
                                <figure
                                  className="container1"
                                  key={index}
                                >
                                  <Card
                                    elevation={2}
                                    sx={{
                                      transition: "all 0.2s",
                                      boxShadow: "none",
                                      border:
                                        field.selectedImage?.id === image.id
                                          ? "3px solid #3F0E77"
                                          : "none",
                                      cursor: "pointer",
                                      "&:hover": {
                                        transform: "scale(1.02)",
                                        boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                                      },
                                    }}
                                  >
                                    <CardActionArea
                                      onClick={() =>
                                        handleImageSelection(fieldIndex, image)
                                      }
                                    >
                                      <Box
                                        sx={{
                                          position: "relative",
                                        }}
                                      >
                                        {imageLoadingStates[`${fieldIndex}-${image.id}`] !== false && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              top: "50%",
                                              left: "50%",
                                              transform: "translate(-50%, -50%)",
                                              zIndex: 1,
                                            }}
                                          >
                                            <CircularProgress
                                              sx={{ color: "#3F0E77" }}
                                              size={20}
                                            />
                                          </Box>
                                        )}
                                        <img
                                          className="show"
                                          src={image.s3}
                                          alt={`Event Photo ${index + 1}`}
                                          onLoadStart={() => handleImageLoadStart(fieldIndex, image.id)}
                                          onLoad={() => handleImageLoad(fieldIndex, image.id)}
                                          onError={() => handleImageLoad(fieldIndex, image.id)}
                                          style={{
                                            opacity: imageLoadingStates[`${fieldIndex}-${image.id}`] !== false ? 0 : 1,
                                            transition: "opacity 0.3s ease-in-out",
                                          }}
                                        />
                                        {field.selectedImage?.id === image.id && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              top: 8,
                                              right: 8,
                                              bgcolor: "#3F0E77",
                                              borderRadius: "50%",
                                              p: 0.5,
                                              zIndex: 2,
                                            }}
                                          >
                                            <CheckIcon
                                              sx={{
                                                color: "white",
                                                fontSize: 16,
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </Box>
                                    </CardActionArea>
                                  </Card>
                                </figure>
                              ))
                            }
                          </div>
                        )}
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Pagination
                            count={Math.ceil(
                              (field.pagination?.total ||
                                field.eventImages.length) /
                              (field.pagination?.per_page || 50)
                            )}
                            page={field.pagination?.current_page || 1}
                            onChange={(event, page) =>
                              handlePageChange(fieldIndex, event, page)
                            }
                            color="primary"
                            sx={{
                              "& .MuiPaginationItem-root": {
                                color: "#3F0E77",
                              },
                              "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: "#3F0E77",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#2F0A5A",
                                },
                              },
                              "& .MuiPaginationItem-root:hover": {
                                backgroundColor: "#f0f0f0",
                              },
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 4,
                          backgroundColor: "#f8f8f8",
                          borderRadius: "8px",
                          border: "1px dashed #ccc",
                        }}
                      >
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          No images found for this event and BIB number.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Please check your selections or try editing the
                          search.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}
            </Box>
          ))}

          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100vw",
              background: "#fff",
              zIndex: 1300,
              boxShadow: "0 -2px 8px rgba(63,14,119,0.08)",
              borderTop: "1px solid #e0e0e0",
              py: { xs: 2, md: 3 },
              px: { xs: 2, md: 8 },
            }}
          >
            <Box
              sx={{
                maxWidth: "1400px",
                mx: "auto",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {searchPerformed && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedCount}/{eventFields.length} images selected
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handleProceedToCertificate}
                  aria-label="Generate certificate"
                  disabled={loading}
                  sx={{
                    backgroundColor: "#3F0E77",
                    color: "#FFFFFF",
                    textTransform: "none",
                    borderRadius: 1.5,
                    fontWeight: '500',
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.9rem" },
                    padding: {
                      xs: "8px 12px",
                      sm: "10px 16px",
                      md: "10px 24px",
                    },
                    minWidth: { xs: "auto", sm: "140px" },
                    "&:hover": {
                      backgroundColor: "#2d035c",
                      color: "#FFFFFF",
                    },
                    "&:active": {
                      backgroundColor: "#1f013f",
                    },
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={20}
                      sx={{ color: "#FFFFFF" }}
                    />
                  ) : (
                    "Next"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
      <ToastContainer />
    </Container>
  );
};
export default EventCertificateManager;